import { useMemo, useRef, useEffect } from 'react';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api';
import { createPromptEditor } from '@atlassian/editor-ai-injected-editors/prompt-editor';
import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';
import {
	createPageEditorPluginAIOptions,
	createPageCommentEditorPluginAIOptions,
} from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import { useAutoApplyState } from '@atlassian/creation-settings';

import { fg } from '@confluence/feature-gating';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useEditorRovoButtonActions } from '@confluence/edit-page-rovo-config-provider/entry-points/useEditorRovoButtonActions.ts';

// eslint-disable-next-line import/no-cycle
import { useLinkingFeatures } from '../components/EditorLinkingFeatures.tsx';

import { useMentionProvider } from './useMentionProvider.ts';

export type Sentiment = 'good' | 'bad' | 'unknown';

type EditorPluginAIOptions = Parameters<typeof createPageEditorPluginAIOptions>[0];
type HandleFeedbackSubmission = NonNullable<EditorPluginAIOptions>['handleFeedbackSubmission'];

export type HandleFeedbackSubmissionArgs = Parameters<NonNullable<HandleFeedbackSubmission>>[0];

interface ConfluenceEditorPluginAIOptions {
	appearance?: 'full-page' | 'page-comment';
	/**
	 * Triggers when "thumbs up/down" feedback buttons are is clicked - probably
	 * want to start a survey (freeform field) at this point to collect any
	 * further details.
	 *
	 * Sentiment shape/details may change in future, we may expose that information
	 * more directly as explicit data shapes, but abstracting away to events
	 * for now to get an early iteration working.
	 */

	onFeedbackClicked?: (props: HandleFeedbackSubmissionArgs) => void;
	/**
	 * Triggers when the AI plugin enters a review state (response finished streaming)
	 */
	onReviewStateEntered?: () => void;
	/**
	 * Triggers when the AI plugin experience is complete, e.g. user has inserted
	 * content into the page, ending the AI experience.
	 */
	onExperienceComplete?: () => void;
	/**
	 * Opt in status of AI
	 */
	aiGlobalOptIn: EditorPluginAIOptions['aiGlobalOptIn'];

	AIButtonWrapper?: React.FC<{ children: React.ReactNode }>;
	/**
	 * Used to generate hash for pages channelID with use of Rovo agents.
	 */
	getChannelVariables?: EditorPluginAIOptions['getChannelVariables'];

	/**
	 * Triggered when the document has been changed by a Rovo agent.
	 */
	onDocChangeByAgent?: EditorPluginAIOptions['onDocChangeByAgent'];

	/**
	 * Triggered when the AI provider interaction starts.
	 */
	onAIProviderChanged?: EditorPluginAIOptions['onAIProviderChanged'];

	providers?: EditorPluginAIOptions['providers'];

	/**
	 * contentId (aka pageId or objectId) to use in audit log settings.
	 */
	contentId?: string;

	/**
	 * Triggered when the AI plugin has removed annotations.
	 */
	onAnnotationsRemoved?: EditorPluginAIOptions['onAnnotationsRemoved'];

	getUserPreferences?: () => EditorPluginAIOptions['getUserPreferences'];

	usePlatformPrompt?: boolean;
}

const CARD_OPTIONS = { allowDatasource: true };

const defaultOptions: ConfluenceEditorPluginAIOptions = {
	appearance: 'full-page',
	onFeedbackClicked: () => {},
	onReviewStateEntered: () => {},
	onExperienceComplete: () => {},
	aiGlobalOptIn: { status: 'disabled', triggerOptInFlow: () => {} },
};

export const useEditorPluginAIOptions = (
	options?: ConfluenceEditorPluginAIOptions,
): ReturnType<typeof createPageEditorPluginAIOptions> => {
	const optionsToUse = options || defaultOptions;

	const createEditorPluginAIOptions = useMemo(
		() =>
			optionsToUse.appearance === 'page-comment'
				? createPageCommentEditorPluginAIOptions
				: createPageEditorPluginAIOptions,
		[optionsToUse.appearance],
	);

	const optionsToUseRef = useRef(optionsToUse);
	optionsToUseRef.current = optionsToUse;

	const { cloudId, userId } = useSessionData();

	// Memoize session data to avoid recreating the object on every render
	const sessionData = useMemo(
		() => (userId && cloudId ? { userId, cloudId } : undefined),
		[userId, cloudId],
	);

	// Use the dedicated hook for auto-apply state management
	const autoApplyState = useAutoApplyState(
		fg('enable_auto_apply_toggling') ? sessionData : undefined,
	);
	const isInDocumentStreamingAllowed = fg('enable_auto_apply_toggling')
		? (autoApplyState?.enabled ?? false)
		: false;
	const refresh = autoApplyState?.refresh;

	// Listen for changes from other parts of the app
	useEffect(() => {
		if (!fg('enable_auto_apply_toggling') || !refresh) {
			return;
		}

		const handleAutoApplyChange = () => {
			// Refresh the state from backend/localStorage when toggle changes
			void refresh();
		};

		window.addEventListener('autoApplyChanged', handleAutoApplyChange);
		return () => window.removeEventListener('autoApplyChanged', handleAutoApplyChange);
	}, [refresh]);

	// Use a ref to avoid recreating the options object on every state update
	const isInDocumentStreamingAllowedRef = useRef(isInDocumentStreamingAllowed);
	isInDocumentStreamingAllowedRef.current = isInDocumentStreamingAllowed;

	const productConfig = useMemo(
		() => ({
			product: 'confluence',
			cloudId,
		}),
		[cloudId],
	);
	const linkPicker = useLinkPickerEditorProps(undefined, productConfig);
	const spaceId = useSpaceId();
	const mentionProvider = useMentionProvider(spaceId);

	const linkingProp = useLinkingFeatures(CARD_OPTIONS);

	const PromptEditor = useMemo(
		() =>
			createPromptEditor({
				linkPicker,
				enableLinks: true,
				linking: linkingProp.linking,
				mentionProvider,
				featureFlags: {},
			}),
		[linkingProp.linking, linkPicker, mentionProvider],
	);

	const editorRovoButtonActions = useEditorRovoButtonActions();

	const isAISmartButtonEnabled = useMemo(
		() =>
			optionsToUse.appearance !== 'page-comment' && optionsToUse.aiGlobalOptIn.status === 'enabled',
		[optionsToUse.appearance, optionsToUse.aiGlobalOptIn.status],
	);

	const { setNudges } = useAISmartButtonActions();

	const aiSmartButtonOptions = useMemo(
		() =>
			isAISmartButtonEnabled
				? {
						actions: { ...editorRovoButtonActions, setNudges },
					}
				: undefined,
		[isAISmartButtonEnabled, editorRovoButtonActions, setNudges],
	);

	return useMemo(() => {
		return createEditorPluginAIOptions({
			aiGlobalOptIn: optionsToUseRef.current.aiGlobalOptIn,
			/**
			 * Hide AI button in primary toolbar when AI proactive is enabled or
			 * 	when appearance is full-page and AI smart button is enabled
			 */
			hidePrimaryToolbarButton:
				optionsToUse.appearance === 'full-page' && optionsToUse.aiGlobalOptIn.status === 'enabled',
			generativeAIApiUrl: `${window.location.origin}/gateway/api/editor-ai/v1/generative/ai`,
			handleFeedbackSubmission: async (feedbackMetadata) => {
				try {
					// TODO: EDF-831: Fix and ensure types match with handleFeedbackSubmission
					optionsToUseRef.current.onFeedbackClicked?.(feedbackMetadata);
					return { status: 'submitted' as const };
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
				} catch (error) {
					return { status: 'failed' as const };
				}
			},
			onAnnotationsRemoved: fg('platform_editor_ai_aifc_streaming')
				? optionsToUseRef.current.onAnnotationsRemoved
				: undefined,
			onExperienceEvent: {
				// Thumbs up and down are being deprecated and are no longer used.
				THUMBS_UP: () => {},
				THUMBS_DOWN: () => {},
				REVIEW_STATE_ENTERED: () => {
					optionsToUseRef.current.onReviewStateEntered?.();
				},
				EXPERIENCE_COMPLETE: () => {
					optionsToUseRef.current.onExperienceComplete?.();
				},
			},
			onDocChangeByAgent: (_agent) => {
				// TODO Integrate with confluence
			},
			onAIProviderChanged: (source, aiProvider) => {
				if (optionsToUseRef.current.onAIProviderChanged) {
					optionsToUseRef.current.onAIProviderChanged(source, aiProvider);
				}
			},
			getUserPreferences: () => {
				if (!fg('enable_auto_apply_toggling')) {
					return {};
				}

				return { isInDocumentStreamingAllowed: isInDocumentStreamingAllowedRef.current };
			},
			getChannelVariables: optionsToUseRef.current.getChannelVariables,
			product: 'CONFLUENCE',
			objectId: optionsToUseRef.current.contentId,
			PromptEditor,
			AIButtonWrapper: options?.AIButtonWrapper,
			providers: options?.providers,
			/**
			 * Need to audit logs only for confluence page as of now.
			 */
			auditLogSettings:
				optionsToUse.appearance !== 'page-comment' && optionsToUse.contentId
					? {
							enabled: true,
							eventData: {
								type: 'page',
								pageId: optionsToUse.contentId,
							},
						}
					: { enabled: false },
			...(isAISmartButtonEnabled && {
				aiSmartButtonOptions,
			}),
			...(optionsToUse.usePlatformPrompt && {
				onSubmit: () => {},
			}),
		});
	}, [
		createEditorPluginAIOptions,
		optionsToUse.appearance,
		optionsToUse.contentId,
		PromptEditor,
		options?.AIButtonWrapper,
		options?.providers,
		isAISmartButtonEnabled,
		aiSmartButtonOptions,
		optionsToUse.aiGlobalOptIn.status,
		optionsToUse.usePlatformPrompt,
	]);
};
